<template>
  <div>
    <div class="section-header">
      <h3>Historical Spread</h3>
      <div class="section-header-right ml-sm-0 ml-md-auto">
        <select v-if="Array.isArray(allIsins) && allIsins.length > 1" v-model="selectedISIN" class="subtle-select mr-2">
          <option value="-1" selected>Switch bonds</option>
          <option v-for="item in allIsins" :key="item.isin" :value="item">
            {{ item.bondName || item.isin }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="h-[360px] content-card grid place-content-center">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>
    <Chart v-else :options="chartOptions"></Chart>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from "vue"
import Chart, { type Highcharts } from "@wbim/highcharts-themed"
import ProgressSpinner from "primevue/progressspinner"

import CorpApi from "@/apis/corpApi"
import type { BondInfo } from "@/apis/corpApi"
import type SovereignUniverse from "@/utils/SovereignUniverse"

const sovereignUniverse = inject<SovereignUniverse>("sovereignUniverse")!
const api = new CorpApi()

const props = defineProps<{ issuer: string }>()
const allIsins = ref<BondInfo[]>([])
const selectedISIN = ref<BondInfo>()
const selectedBondSeries = ref<Highcharts.SeriesLineOptions>()
let aggSeries: Highcharts.SeriesLineOptions[] = [] //will not update after initial load
const loading = ref(true)

const chartOptions = computed<Highcharts.Options>(() => {
  const selectedBond = selectedBondSeries.value
  const series = selectedBond ? [selectedBond, ...aggSeries] : aggSeries
  return {
    chart: { type: "line", height: 360 },
    legend: { enabled: true },
    tooltip: { valueDecimals: 1, xDateFormat: "%Y-%m-%d" },
    xAxis: { type: "datetime" },
    yAxis: { title: { text: "Spread" } },
    series
  }
})

function getName(code3: string) {
  const name = sovereignUniverse.fromAlpha3(code3)?.name
  return name
}

onMounted(async () => {
  loading.value = true
  const issuerInfo = await api.loadIssuerInfo(props.issuer)
  const { isins, sub_credit_bucket, country } = issuerInfo
  allIsins.value = await api.loadIsinsInfo(isins)
  const countryName = getName(country)

  aggSeries = await Promise.all(
    [countryName, sub_credit_bucket ?? "NR"].map(async (instrument) => {
      return {
        type: "line",
        name: instrument,
        data: await api.loadAggSeries(instrument)
      }
    })
  )

  const bonds = await api.loadDefaultBonds(props.issuer)
  let index = -1
  if (Array.isArray(bonds) && bonds.length > 0) {
    index = allIsins.value.findIndex((d) => d.isin === bonds[0])
  }
  selectedISIN.value = allIsins.value[Math.max(index, 0)]
  loading.value = false
})

watch(
  () => selectedISIN.value,
  async function (selected) {
    if (!selected || !selected.isin) {
      return
    }
    selectedBondSeries.value = {
      type: "line",
      name: selected.bondName,
      data: await api.loadBondSeries(selected.isin)
    }
    try {
      api.updateDefaultBonds(props.issuer, selected.isin)
    } catch {
      console.error("failed to update default bonds")
    }
  },
  { immediate: true }
)
</script>
